import { Price } from "interfaces/KYDTicket";
import type { CampaignIntent } from "./CampaignIntent";
import { KYDAdminCampaign, KYDEvent, KYDEventSummary } from "./KYDEvent";
import { MetaAdSetLearningStageInfo } from "./MetaTypes";

export type {
  Action,
  ActionableCampaignIntent,
  CampaignIntent,
  CampaignIntentItemMetadata,
  CampaignIntentStatus,
  CanonicalActionType,
  IntentEvaluationContext as InsightEvaluationContext,
  IntentReasoning as InsightReasoning,
  IntentSchemaVersion as InsightSchemaVersion,
  IntentSourceEntity,
  IntentExplanation,
  IntentSeverity,
  IntentTargetEntity,
  IntentType,
  OrganizationAdsIntentsBaselines,
  SuggestedActionCommon,
  SuggestedActionIncreaseBudget,
  SuggestedActionItem,
  SuggestedActionPauseAdset,
  SuggestedActionRefreshCreative,
} from "./CampaignIntent";
export {
  ENTITY_TYPES,
  INTENT_TYPES,
  INTENT_TYPE_ALLOWED_ENTITY_TYPES,
  INTENT_TYPE_LABELS,
  isActionableIntent,
} from "./CampaignIntent";
export type { IntentEntityType } from "./CampaignIntent";

// ============================================================================
// CONSTANTS AND SCHEMA FIELDS
// ============================================================================

export const AUDIENCE_SCHEMA_FIELDS = ["FN", "LN", "COUNTRY", "EMAIL", "PHONE"];

// ============================================================================
// PLATFORM AND POSITION ENUMS
// ============================================================================

export enum PublisherPlatforms {
  "facebook" = "facebook",
  "instagram" = "instagram",
  "messenger" = "messenger",
  "audience_network" = "audience_network",
}

export enum FacebookPositions {
  "feed" = "feed",
  "facebook_reels_overlay" = "facebook_reels_overlay",
  "profile_feed" = "profile_feed",
  "video_feeds" = "video_feeds",
  "instream_video" = "instream_video",
  "marketplace" = "marketplace",
  "facebook_reels" = "facebook_reels",
  "story" = "story",
}

export enum InstagramPositions {
  /**
   * Legacy value; Meta rejects `feed` in `instagram_positions` (targeting and creatives).
   * Use {@link InstagramPositions.stream} for Instagram feed; kept for stored payloads.
   */
  "feed" = "feed",
  /** Instagram main feed / in-stream placements (Graph API value for “feed”). */
  "stream" = "stream",
  "ig_search" = "ig_search",
  "explore" = "explore",
  "explore_home" = "explore_home",
  "profile_feed" = "profile_feed",
  "story" = "story",
  "reels" = "reels",
}

export enum MessengerPositions {
  "messenger_home" = "messenger_home",
  "story" = "story",
}

export enum AudienceNetworkPositions {
  "classic" = "classic",
}

export enum DevicePlatforms {
  "mobile" = "mobile",
  "desktop" = "desktop",
}

// ============================================================================
// STATUS AND TYPE ENUMS
// ============================================================================

export enum AdPlatform {
  "META" = "META",
}

export enum CampaignStatus {
  "ACTIVE" = "ACTIVE",
  "PAUSED" = "PAUSED",
  "COMPLETED" = "COMPLETED",
  "ARCHIVED" = "ARCHIVED",
  "FROZEN" = "FROZEN", // If changed from meta and can't be changed from our side
  "DRAFT" = "DRAFT", // If not yet published
}

export enum AdSetStatus {
  "ACTIVE" = "ACTIVE",
  "PAUSED" = "PAUSED",
  "DELETED" = "DELETED",
  "ARCHIVED" = "ARCHIVED",
  "TO_DELETE" = "TO_DELETE", // If marked to deletion
  "FROZEN" = "FROZEN", // If changed from meta and can't be changed from our side
  "DRAFT" = "DRAFT", // If not yet published
}

/** Server-driven performance classification for campaign / adset / ad. Used for badges. */
export enum PerformanceTier {
  "HIGH_PERFORMANCE" = "HIGH_PERFORMANCE",
  "UNDERPERFORMING" = "UNDERPERFORMING",
  "ON_TRACK" = "ON_TRACK",
  "TOO_EARLY_TO_TELL" = "TOO_EARLY_TO_TELL",
}

/** Performance tier plus optional one-line explanation (e.g. finding summary). */
export interface PerformanceInfo {
  tier: PerformanceTier;
  explanation?: string;
}

export enum OptimizationGoal {
  "LANDING_PAGE_VIEWS" = "IMPRESSIONS",
  "CONVERSIONS" = "OFFSITE_CONVERSIONS",
  "VALUE" = "VALUE",
}

export enum CampaignType {
  "AWARENESS" = "awareness",
  "CONVERSION" = "conversion",
}

/** Channel discriminator for campaigns in the marketing table (EMAIL vs ADS). */
export type CampaignChannelType = "EMAIL" | "ADS";

export enum CampaignDataPrefix {
  EMAIL = "CAMPAIGN#EMAIL",
  ADS = "CAMPAIGN#ADS",
}

/** Data prefix for ads campaign records in the marketing table (data attribute). */
export const CAMPAIGN_DATA_PREFIX_ADS = `${CampaignDataPrefix.ADS}#`;

/** Included related resources for campaign fetch responses. */
export type MarketingCampaignIntentsIncludeMode = "all" | "regular" | "llm";

/**
 * Include options for `AdsManagerService.fetchCampaign` (customer HTTP API maps query params into this shape).
 *
 * Omit `intents` to skip loading separate intent partition rows (lighter reads for publish/metrics paths that only need structure).
 *
 * **Finding server call sites:** `rg 'adsManagerService\.fetchCampaign\(' --glob '*.ts'` from repo root (ignore `common/dist`).
 */
export interface MarketingCampaignFetchInclude {
  event?: boolean;
  intents?: MarketingCampaignIntentsIncludeMode;
}

/** Bare minimum fields shared by all campaign records (ads and email) in metadata. */
export interface SharedCampaignFields {
  id?: string;
  type?: CampaignChannelType;
  organization_id: string;
  name: string;
  status?: string;
  created_at?: string;
}

export enum RatioType {
  "square" = "square",
  "portrait" = "portrait",
}

export enum AdFormat {
  "SINGLE_IMAGE" = "SINGLE_IMAGE",
  "SINGLE_VIDEO" = "SINGLE_VIDEO",
  "CAROUSEL" = "CAROUSEL",
  "AUTOMATIC_FORMAT" = "AUTOMATIC_FORMAT",
}

// ============================================================================
// RATIO POSITIONS CONSTANT
// ============================================================================

export const RatioPositions = {
  [RatioType.portrait]: {
    [PublisherPlatforms.facebook]: [
      FacebookPositions.story,
      FacebookPositions.facebook_reels,
      FacebookPositions.facebook_reels_overlay,
    ],
    [PublisherPlatforms.instagram]: [
      InstagramPositions.story,
      InstagramPositions.reels,
    ],
  },
  [RatioType.square]: {
    [PublisherPlatforms.facebook]: [
      FacebookPositions.feed,
      FacebookPositions.video_feeds,
      FacebookPositions.instream_video,
    ],
    [PublisherPlatforms.instagram]: [
      // Square / feed-like placements: stream is Instagram feed; bundled with Explore in defaults.
      InstagramPositions.stream,
      InstagramPositions.explore_home,
      InstagramPositions.profile_feed,
      InstagramPositions.explore,
    ],
  },
};

// ============================================================================
// CORE MARKETING INTERFACES
// ============================================================================

export interface MarketingAccount {
  id: string;
  name: string;
}

export type LookalikeRatio = "0-1" | "0-3" | "0-5";

/** Meta budget-schedule window value semantics (campaign `budget_schedules` edge). */
export type MarketingCampaignBudgetScheduleValueType =
  | "ABSOLUTE"
  | "MULTIPLIER";

/**
 * Derived row for Meta campaign `budget_schedule_specs` (create/update) and related helpers.
 * Times are ISO 8601; convert to unix only at the Meta API boundary.
 */
export interface MarketingCampaignBudgetScheduleWindow {
  time_start: string;
  time_end: string;
  budget_value: number;
  budget_value_type: MarketingCampaignBudgetScheduleValueType;
}

/** One phased budget segment (wall times as ISO 8601; `amount` minor units on persisted campaign). */
export interface MarketingCampaignBudgetSegment {
  time_start: string;
  time_end: string;
  amount: Price;
  /** Meta Graph node id for this segment's `budget_schedules` row when it is a boosted phase. */
  external_id?: string;
}

export type MarketingCampaignBudgetSimple = {
  type: "daily" | "lifetime";
  amount: Price;
};

/**
 * Campaign uses phased budget: contiguous segments with absolute caps (KYD model).
 * At publish, Meta gets `budget_schedule_specs` on campaign create/update for higher phases; the campaign uses `daily_budget`
 * for the minimum daily-equivalent segment rate when those schedules exist (Meta API requirement).
 * That daily-equivalent uses {@link scheduledSegmentElapsedDayUnits} with the campaign’s ad-set
 * schedule timezone when available (see {@link timezoneFromCampaignAdsets}).
 * KYD still surfaces a single scheduled flow.
 */
export type MarketingCampaignBudgetScheduled = {
  type: "scheduled";
  /**
   * Phased caps (total per phase). Meta base amount is derived from the minimum
   * per-phase daily-equivalent amount (`segment_amount / phase_duration_days`) and sent as
   * `daily_budget` when schedules post, else `lifetime_budget`.
   */
  segments: MarketingCampaignBudgetSegment[];
};

export type MarketingCampaignBudget =
  | MarketingCampaignBudgetSimple
  | MarketingCampaignBudgetScheduled;

export interface MarketingCampaign extends SharedCampaignFields {
  id?: string;
  type?: CampaignChannelType;
  external_id?: string;
  source: "USER" | "SYSTEM" | "EXTERNAL";

  event_ids: KYDEvent["id"][];
  organization_id: KYDEvent["organization_id"];

  execution_id?: string;
  publish_errors?: string[];

  account_id: string;

  name: string;

  budget?: MarketingCampaignBudget;

  // pixel_id?: string; -- check how this will affect audiences and custom events

  campaign_type: CampaignType;
  adsets?: MarketingAdSet[];

  status?: CampaignStatus;
  metrics?: KYDAdminCampaign;

  targeting?: {
    custom_audiences?: MarketingCustomAudience[];
  };

  user_updated_at?: string;
  updated_time?: string;
  created_time?: string;
  created_at?: string;
  platform?: string;

  /** Intents (recommended actions) for this ads campaign; result of the ads-campaign-intents step function. */
  intents?: CampaignIntent[];

  /** Server-driven performance classification; used for performance badge (e.g. High performance / Underperforming). */
  performance?: PerformanceInfo;
}

export interface MarketingCustomAudience {
  id?: string;
  external_id: string;
  name?: string;
  type: "kyd" | "page_lookalike" | "page_engagers" | "csv" | "meta";
  error?: any;
  metadata?: {
    page_type?: "facebook" | "instagram";
    page_id?: string;
    page_name?: string;
    account_id: string;

    description?: string;
    file_key?: string;
    schema_fields?: typeof AUDIENCE_SCHEMA_FIELDS;
    mapping?: string[];

    // If segment persisted, use segment_id
    // otherwise, use query_execution_id
    segment_id?: string;
    query_execution_id?: string;

    genres?: string[];
    artists?: string[];
    events?: string[];
    lookalike_name?: string;
    lookalike_ratio?: LookalikeRatio;

    delivery_status?: {
      code: number;
      description?: string;
    };
    subtype?: string;
    approximate_count_upper_bound?: number;
    approximate_count_lower_bound?: number;

    created_time?: string;
    updated_time?: string;
  };
}

export interface MarketingAdSet {
  id: string;
  status: AdSetStatus;
  external_id?: string | null;
  source: "USER" | "SYSTEM" | "EXTERNAL";

  /** @deprecated Use parent_id instead */
  campaign_id?: string;
  parent_id: string;

  account_id: string;

  budget?: {
    type: "daily" | "lifetime";
    amount: Price;
  };

  custom_conversion_id?: string;

  name: string;
  event_id: string;

  schedule?: {
    start_time?: string;
    stop_time?: string;
    timezone?: string;
  };

  targeting?: {
    locations: {
      countries?: { key: string; value: string }[];
      geo_markets?: { key: string; value: string; country: string }[];
      regions?: { key: string; value: string }[];
      cities?: { key: string; value: string }[];
    };
    demographics: {
      age_min?: number;
      age_max?: number;
      genders?: string[];
    };
    interests?: { key: string; value: string }[];
    device_platforms?: { key: string; value: string }[];

    custom_audiences?: MarketingCustomAudience[];

    /** Meta ad set exclusions; `external_id` is the Meta custom audience id. */
    excluded_custom_audiences?: MarketingCustomAudience[];
  };

  audience_type: "advantage" | "manual";

  tracking: {
    pixel_id?: string;
    custom_conversion_event?: boolean;
  };

  ads?: MarketingAd[];

  platform: AdPlatform;
  metrics?: Record<string, any>;

  learning?: MetaAdSetLearningStageInfo;
  created_at?: string;
  updated_at?: string;
  user_updated_at?: string;
  timezone: string;

  error?:
    | any
    | {
        message: string;
      };

  /** Server-driven performance classification; used for performance badge. */
  performance?: PerformanceInfo;

  /** Intents that target this ad set (stored with the entity). */
  intents?: CampaignIntent[];
}

export interface MarketingAd {
  id?: string;
  external_id?: string;
  name?: string;
  status?: AdSetStatus;
  creative?: MarketingAdCreative;
  metrics?: KYDAdminCampaign;

  /** Server-driven performance classification; used for performance badge. */
  performance?: PerformanceInfo;

  /** Intents that target this ad (stored with the entity). */
  intents?: CampaignIntent[];
}

// ============================================================================
// CREATIVE AND MEDIA INTERFACES
// ============================================================================

export interface AdCreativeImage {
  type?: RatioType;
  file_key?: string;
  src: string;
  hash?: string;
}

export interface AdCreativeVideo {
  external_id?: string;
  type?: RatioType;
  file_key?: string;
  src: string;
  thumbnail_src: string;
  // Facebook-specific fields
  video_id?: string;
  source?: string;
}

export interface AdCreativeText {
  text: string;
}

export enum CallToActionType {
  OPEN_LINK = "OPEN_LINK",
  LIKE_PAGE = "LIKE_PAGE",
  SHOP_NOW = "SHOP_NOW",
  PLAY_GAME = "PLAY_GAME",
  INSTALL_APP = "INSTALL_APP",
  USE_APP = "USE_APP",
  CALL = "CALL",
  CALL_ME = "CALL_ME",
  VIDEO_CALL = "VIDEO_CALL",
  INSTALL_MOBILE_APP = "INSTALL_MOBILE_APP",
  USE_MOBILE_APP = "USE_MOBILE_APP",
  MOBILE_DOWNLOAD = "MOBILE_DOWNLOAD",
  BOOK_TRAVEL = "BOOK_TRAVEL",
  LISTEN_MUSIC = "LISTEN_MUSIC",
  WATCH_VIDEO = "WATCH_VIDEO",
  LEARN_MORE = "LEARN_MORE",
  SIGN_UP = "SIGN_UP",
  DOWNLOAD = "DOWNLOAD",
  WATCH_MORE = "WATCH_MORE",
  NO_BUTTON = "NO_BUTTON",
  VISIT_PAGES_FEED = "VISIT_PAGES_FEED",
  CALL_NOW = "CALL_NOW",
  APPLY_NOW = "APPLY_NOW",
  CONTACT = "CONTACT",
  BUY_NOW = "BUY_NOW",
  GET_OFFER = "GET_OFFER",
  GET_OFFER_VIEW = "GET_OFFER_VIEW",
  BUY_TICKETS = "BUY_TICKETS",
  UPDATE_APP = "UPDATE_APP",
  GET_DIRECTIONS = "GET_DIRECTIONS",
  BUY = "BUY",
  SEND_UPDATES = "SEND_UPDATES",
  MESSAGE_PAGE = "MESSAGE_PAGE",
  DONATE = "DONATE",
  SUBSCRIBE = "SUBSCRIBE",
  SAY_THANKS = "SAY_THANKS",
  SELL_NOW = "SELL_NOW",
  SHARE = "SHARE",
  DONATE_NOW = "DONATE_NOW",
  GET_QUOTE = "GET_QUOTE",
  CONTACT_US = "CONTACT_US",
  ORDER_NOW = "ORDER_NOW",
  START_ORDER = "START_ORDER",
  ADD_TO_CART = "ADD_TO_CART",
  VIEW_CART = "VIEW_CART",
  VIEW_IN_CART = "VIEW_IN_CART",
  VIDEO_ANNOTATION = "VIDEO_ANNOTATION",
  RECORD_NOW = "RECORD_NOW",
  INQUIRE_NOW = "INQUIRE_NOW",
  CONFIRM = "CONFIRM",
  REFER_FRIENDS = "REFER_FRIENDS",
  REQUEST_TIME = "REQUEST_TIME",
  GET_SHOWTIMES = "GET_SHOWTIMES",
  LISTEN_NOW = "LISTEN_NOW",
  TRY_DEMO = "TRY_DEMO",
  WOODHENGE_SUPPORT = "WOODHENGE_SUPPORT",
  SOTTO_SUBSCRIBE = "SOTTO_SUBSCRIBE",
  FOLLOW_USER = "FOLLOW_USER",
  RAISE_MONEY = "RAISE_MONEY",
  SEE_SHOP = "SEE_SHOP",
  GET_DETAILS = "GET_DETAILS",
  FIND_OUT_MORE = "FIND_OUT_MORE",
  VISIT_WEBSITE = "VISIT_WEBSITE",
  BROWSE_SHOP = "BROWSE_SHOP",
  EVENT_RSVP = "EVENT_RSVP",
  WHATSAPP_MESSAGE = "WHATSAPP_MESSAGE",
  FOLLOW_NEWS_STORYLINE = "FOLLOW_NEWS_STORYLINE",
  SEE_MORE = "SEE_MORE",
  BOOK_NOW = "BOOK_NOW",
  FIND_A_GROUP = "FIND_A_GROUP",
  FIND_YOUR_GROUPS = "FIND_YOUR_GROUPS",
  PAY_TO_ACCESS = "PAY_TO_ACCESS",
  PURCHASE_GIFT_CARDS = "PURCHASE_GIFT_CARDS",
  FOLLOW_PAGE = "FOLLOW_PAGE",
  SEND_A_GIFT = "SEND_A_GIFT",
  SWIPE_UP_SHOP = "SWIPE_UP_SHOP",
  SWIPE_UP_PRODUCT = "SWIPE_UP_PRODUCT",
  SEND_GIFT_MONEY = "SEND_GIFT_MONEY",
  PLAY_GAME_ON_FACEBOOK = "PLAY_GAME_ON_FACEBOOK",
  GET_STARTED = "GET_STARTED",
  OPEN_INSTANT_APP = "OPEN_INSTANT_APP",
  AUDIO_CALL = "AUDIO_CALL",
  GET_PROMOTIONS = "GET_PROMOTIONS",
  JOIN_CHANNEL = "JOIN_CHANNEL",
  MAKE_AN_APPOINTMENT = "MAKE_AN_APPOINTMENT",
  ASK_ABOUT_SERVICES = "ASK_ABOUT_SERVICES",
  BOOK_A_CONSULTATION = "BOOK_A_CONSULTATION",
  GET_A_QUOTE = "GET_A_QUOTE",
  BUY_VIA_MESSAGE = "BUY_VIA_MESSAGE",
  ASK_FOR_MORE_INFO = "ASK_FOR_MORE_INFO",
  CHAT_WITH_US = "CHAT_WITH_US",
  VIEW_PRODUCT = "VIEW_PRODUCT",
  VIEW_CHANNEL = "VIEW_CHANNEL",
  GET_IN_TOUCH = "GET_IN_TOUCH",
  WATCH_LIVE_VIDEO = "WATCH_LIVE_VIDEO",
  SHOP_WITH_AI = "SHOP_WITH_AI",
}

export interface AdCreativeLink {
  url: string;
  display_url?: string;
}

export interface AdCreativeCallToAction {
  type: CallToActionType;
  link: string;
}

export interface AssetCustomizationRule {
  priority: number;
  customization: {
    age_min?: number;
    age_max?: number;
    publisher_platforms?: PublisherPlatforms[];
  };
}

// Platform-specific customization rules
export interface FacebookCustomizationRule extends AssetCustomizationRule {
  customization: AssetCustomizationRule["customization"] & {
    facebook_positions?: FacebookPositions[];
    messenger_positions?: MessengerPositions[];
  };
}

export interface InstagramCustomizationRule extends AssetCustomizationRule {
  customization: AssetCustomizationRule["customization"] & {
    instagram_positions?: InstagramPositions[];
  };
}

// Union type for all platform customization rules
export type PlatformCustomizationRule =
  | FacebookCustomizationRule
  | InstagramCustomizationRule;

export interface MarketingAdCreative {
  id?: string;
  external_id?: string; // creative id from meta
  name?: string;
  status?: string;

  identity?: {
    [PublisherPlatforms.facebook]?: {
      page_id: string;
    };
    [PublisherPlatforms.instagram]?: {
      instagram_actor_id: string;
    };
  };

  user_updated_at?: string;

  ad_formats: AdFormat[];

  // Dynamic content assets with multiple variations
  assets?: {
    images?: AdCreativeImage[];
    videos?: AdCreativeVideo[];
    bodies?: AdCreativeText[];
    titles?: AdCreativeText[];
    descriptions?: AdCreativeText[];
    links?: AdCreativeLink[];
    call_to_actions?: AdCreativeCallToAction[];
  };

  // Content customization rules for dynamic targeting
  customization_rules?: PlatformCustomizationRule[];
}

// ============================================================================
// TRANSFORMATION AND UTILITY INTERFACES
// ============================================================================

// Transformation utilities
export interface CreativeTransformationOptions {
  platform?: PublisherPlatforms;
  includeAssetFeedSpec?: boolean;
  includeObjectStorySpec?: boolean;
  includeLegacyFields?: boolean;
}

// ============================================================================
// DISPLAY INTERFACES
// ============================================================================

export interface DisplayMarketingCampaign extends MarketingCampaign {
  adsets?: DisplayMarketingAdSet[];
  intents?: CampaignIntent[];
  event?: KYDEventSummary;
  /**
   * One schedule per campaign, derived at the output boundary
   * ({@link AdsManagerService.formatCampaign}) from {@link deriveDisplayCampaignSchedule}:
   * picks the first non-ended adset's schedule, falling back to min-start / max-stop across
   * all adsets when every adset has ended so historical campaign views still render.
   *
   * Read-only on the wire. Schedule writes flow through `updateGuidedEditPayload`, which
   * fans the value back onto non-ended adsets in `applyGuidedEditScheduleToCampaign`.
   */
  schedule?: {
    start_time: string;
    stop_time: string;
    timezone?: string;
  };
}

export interface DisplayMarketingAdSet
  extends Omit<MarketingAdSet, "ads" | "schedule"> {
  display_budget?: {
    type: "daily" | "lifetime";
    amount: string;
  };
  ads?: DisplayMarketingAd[];
  /**
   * @deprecated For the campaign-level run window (display dates, scheduled-budget
   * flight bounds), read {@link DisplayMarketingCampaign.schedule} instead — it is
   * derived once at the output boundary from non-ended adsets and reflects the
   * one-schedule-per-campaign invariant. Per-adset reads remain valid for per-adset
   * semantics only (e.g. `isAdsetScheduleEnded`, preserving frozen ended-adset
   * values on partial-adset updates).
   */
  schedule?: MarketingAdSet["schedule"];
  custom_audience_files?: {
    id?: string;
    external_id?: string;
    file_key: string;
    name?: string;
    description?: string;
    schema_fields?: string[];
  }[];
}

export interface DisplayMarketingAd extends MarketingAd {}

// ============================================================================
// CREATE/UPDATE INTERFACES
// ============================================================================

/**
 * Campaign/adset budget `{ type, amount }` shared by create/update requests and edit flow.
 *
 * - **Persisted** `Price` and **guided-edit** GET/PUT: `amount.amount` is **minor** units (e.g. cents for USD).
 * - **`CreateMarketingAdsetRequest`** from the dashboard adset form/API: **minor** units (form dollars are converted before send).
 * - **Campaign** `createCampaign` / `updateCampaign` HTTP handlers still accept **major** units in this shape and
 *   convert via {@link AdsetTransformer.transformToPrice} (legacy).
 * - **`scheduled`**: `segments[].amount` uses **minor** units (same as other persisted money).
 */
export type MarketingCampaignBudgetDineroPayload =
  | {
      type: "daily" | "lifetime";
      amount: {
        amount: number;
        currency: string;
      };
    }
  | {
      type: "scheduled";
      segments: MarketingCampaignBudgetSegment[];
    };

export interface CreateMarketingCampaign
  extends Omit<MarketingCampaign, "budget" | "source"> {
  event_ids: string[];
  budget?: MarketingCampaignBudgetDineroPayload;
}

export interface UpdateMarketingCampaign
  extends Partial<Omit<MarketingCampaign, "budget" | "targeting">> {
  /** Schedule is not a campaign-level write field; use guided-edit schedule updates. */
  custom_audiences?: AudienceData[];
  event_ids?: string[];
  budget?: MarketingCampaignBudgetDineroPayload;
}

export interface CreateMarketingAdSet
  extends Omit<MarketingAdSet, "id" | "status" | "optimization_goal"> {
  campaign_type: CampaignType;
}

export interface UpdateMarketingAdSet
  extends Partial<Omit<MarketingAdSet, "campaign_id">> {}

export interface CreateMarketingAdCreative
  extends Omit<MarketingAdCreative, "external_id"> {}

// ============================================================================
// REQUEST INTERFACES
// ============================================================================

/**
 * Creative payload for {@link CreateMarketingAdRequest} (full ad create / bulk flows).
 * Guided and launch HTTP bodies carry {@link MarketingAdCreativeGuidedWireBase} plus
 * placement URLs/keys; services map those into this shape (CTA, link, `media`).
 */
export interface CreateMarketingAdCreativeShape {
  name: string;
  primary_text: string;
  call_to_action: string;
  link: string;

  media: {
    images: AdCreativeImage[];
    videos: AdCreativeVideo[];
  };
}

/**
 * Copy fields shared between guided-edit / launch JSON and `creative` on
 * {@link CreateMarketingAdRequest} before CTA, link, and normalized media exist.
 */
export type MarketingAdCreativeGuidedWireBase = Pick<
  CreateMarketingAdCreativeShape,
  "primary_text"
> & {
  /** Required on full {@link CreateMarketingAdCreativeShape}; optional on guided wire. */
  name?: CreateMarketingAdCreativeShape["name"];
};

export interface CreateMarketingAdRequest {
  id?: string;
  external_id?: string;
  event_id: string;
  campaign_id: string;
  organization_id: string;
  status?: string;
  parent_id: string;

  creative: CreateMarketingAdCreativeShape;
}

export interface CreateCustomAudienceRequestBase {
  id?: string;
  external_id?: string;
  name?: string;
}

// ============================================================================
// AUDIENCE DATA TYPES
// ============================================================================

export interface KYDAudienceData extends CreateCustomAudienceRequestBase {
  description?: string;
  created_at?: string;
  audience_type: "kyd";
  genres?: string[];
  artists?: string[];
  event_ids?: string[];
  segment_id?: string;
  lookalike?: {
    name: string;
    ratio: LookalikeRatio;
  };
}

export interface CsvAudienceData extends CreateCustomAudienceRequestBase {
  description: string;
  file_key: string;
  mapping: string[];
  schema_fields: string[];
  audience_type: "csv";
  created_at?: string;
}

export interface PageLookalikeAudienceData
  extends CreateCustomAudienceRequestBase {
  name: string;
  description: string;
  page_type?: "facebook" | "instagram";
  page_id: string;
  page_name: string;
  lookalike?: {
    name: string;
    ratio: LookalikeRatio;
  };
  audience_type: "page_lookalike";
  created_at?: string;
}

export interface PageEngagersAudienceData
  extends CreateCustomAudienceRequestBase {
  name: string;
  description: string;
  page_type?: "facebook" | "instagram";
  page_id: string;
  page_name: string;
  audience_type: "page_engagers";
  created_at?: string;
}

export interface MetaAudienceData extends CreateCustomAudienceRequestBase {
  description: string;
  subtype: string;
  time_created?: string;
  time_updated?: string;
  data_source?: {
    type: string;
    sub_type: string;
  };
  delivery_status?: {
    code: number;
    description?: string;
  };
  approximate_count_upper_bound?: number;
  approximate_count_lower_bound?: number;
  audience_type: "meta";
  created_at?: string;
}

export type AudienceData =
  | KYDAudienceData
  | CsvAudienceData
  | PageLookalikeAudienceData
  | PageEngagersAudienceData
  | MetaAudienceData;

export interface CreateMarketingCampaignRequest {
  id?: string;
  event_ids: string[];
  name: string;
  budget?: MarketingCampaignBudgetDineroPayload;
  account_id: string;
  custom_audiences?: AudienceData[];
  campaign_type: CampaignType;
}

export interface CreateMarketingAdCreativeRequest {
  event_id?: string;
  organization_id: string;
  campaign_id: string;
  parent_id: string;
  ads: CreateMarketingAdRequest[];
}

/**
 * Input type for the updateAdcreative function
 */
export interface UpdateAdCreativeRequest {
  event_id?: string;
  organization_id: string;
  campaign_id: string;
  parent_id: string;
  identity?: {
    [PublisherPlatforms.facebook]?: {
      page_id: string;
    };
    [PublisherPlatforms.instagram]?: {
      instagram_actor_id: string;
    };
  };
  ads: UpdateAdCreativeAd[];
}

/**
 * Individual ad data for updateAdcreative
 */
export interface UpdateAdCreativeAd {
  id?: string;
  external_id?: string;
  event_id?: string;
  campaign_id: string;
  organization_id: string;
  status?: string;
  parent_id: string;
  creative: UpdateAdCreativeCreative;
  ad_formats: AdFormat[];
  identity?: {
    [PublisherPlatforms.facebook]?: {
      page_id: string;
    };
    [PublisherPlatforms.instagram]?: {
      instagram_actor_id: string;
    };
  };
}

/**
 * Creative data for updateAdcreative
 */
export interface UpdateAdCreativeCreative {
  name: string;
  primary_text: string;
  call_to_action: string;
  link: string;
  media: UpdateAdCreativeMedia;
  facebook_placements?: FacebookPositions[];
  instagram_placements?: InstagramPositions[];
}

/**
 * Media data for updateAdcreative
 */
export interface UpdateAdCreativeMedia {
  images: UpdateAdCreativeImage[];
  videos: UpdateAdCreativeVideo[];
}

/**
 * Image data for updateAdcreative
 */
export interface UpdateAdCreativeImage {
  src?: string;
  type?: RatioType;
  hash?: string;
  file_key?: string;
}

/**
 * Video data for updateAdcreative
 */
export interface UpdateAdCreativeVideo {
  src?: string;
  thumbnail_src?: string;
  type?: RatioType;
  external_id?: string;
  file_key?: string;
}

/**
 * `targeting.locations` for {@link CreateMarketingAdsetRequest}.
 * Guided REST `GuidedEditTargetingLocations` in `adsManagerGuidedEdit.ts` widens
 * `geo_markets` items with optional `name`.
 */
export interface MarketingAdsetTargetingLocations {
  countries?: { key: string; value: string }[];
  geo_markets?: { key: string; value: string; country: string }[];
  regions?: { key: string; value: string }[];
  cities?: { key: string; value: string }[];
}

export interface CreateMarketingAdsetRequest {
  id?: string;
  external_id?: string;
  event_id?: string;
  name: string;

  parent_id: string;
  account_id: string;

  budget_type?: "campaign" | "adset";

  budget?: MarketingCampaignBudgetDineroPayload;

  schedule?: {
    start_time?: string;
    stop_time?: string;
    timezone?: string;
  };

  campaign_type: CampaignType;

  targeting?: {
    locations: MarketingAdsetTargetingLocations;
    demographics: {
      age_min?: number;
      age_max?: number;
      genders?: string;
    };
    interests?: { key: string; value: string }[];
    device_platforms?: { key: string; value: string }[];

    custom_audiences?: AudienceData[];
  };

  audience_type: "advantage" | "manual";

  // placements: {
  //   [PublisherPlatforms.facebook]: {
  //     positions: FacebookPositions[];
  //   };
  //   [PublisherPlatforms.instagram]: {
  //     positions: InstagramPositions[];
  //   };
  // };

  custom_audience_files?: {
    id?: string;
    file_key: string;
    name?: string;
    description?: string;
    external_id?: string;
    schema_fields?: typeof AUDIENCE_SCHEMA_FIELDS;
  }[];

  tracking: {
    pixel_id?: string;
    custom_conversion_event?: boolean;
  };

  platform: AdPlatform;

  timezone: string;
  status?: AdSetStatus;
}
